import React, { useMemo, useEffect, useState } from 'react';
import { format } from 'date-fns';
import StackedBarChart, { StackedDataset } from '@/components/charts/StackedBarChart';
import ChartDateLimiter from '@/components/charts/ChartDateLimiter';
import { keepFirstEntryPerMonth } from '@/utils/commonUtils';
import { formatDateLabel, parseDateLiteral } from '@/utils/chartAxisUtils';
import { useRsColors } from '@/hooks/useRsColors';
import { createVerticalHoverLinePlugin } from '@/utils/chartPlugins';
import { useTheme } from '@/contexts/ThemeContext';
import { chartColors } from '@/utils/chartColors';

type SegmentConfig = {
  key: string;
  label: string;
  color?: string;
  dataLabelColor?: string;
};

type PolymerKey = 'PE' | 'PP' | 'PS' | 'PVC';

const SEGMENT_CONFIGS: Record<PolymerKey, SegmentConfig[]> = {
  PE: [
    { key: 'blow_molding', label: 'Blow Molding' },
    { key: 'pipe_conduit', label: 'Pipe/Conduit' },
    { key: 'injection_molding', label: 'Injection Molding' },
    { key: 'film', label: 'Film' },
    { key: 'liquid_food_bottles', label: 'Liquid Food Bottles' },
    { key: 'other_segments', label: 'Other' },
  ],
  PP: [
    { key: 'extrusion_total', label: 'Extrusion', color: '#4AAF1B', dataLabelColor: '#000000' },
    { key: 'injection_molding', label: 'Injection Molding' },
    { key: 'consumer_and_institutional_total', label: 'Consumer & Institutional' },
    { key: 'film_total', label: 'Film (≤10 mils)' },
    { key: 'compounders', label: 'Compounders' },
  ],
  PS: [
    { key: 'food_packaging', label: 'Food Packaging' },
    { key: 'building', label: 'Building' },
    { key: 'medical', label: 'Medical' },
    { key: 'other', label: 'Other' },
  ],
  PVC: [
    { key: 'segment_rigid_pipe_tubing', label: 'Pipe & Tubing' },
    { key: 'segment_siding_skirting', label: 'Siding/Skirting' },
    { key: 'segment_extrusion_windows_and_doors', label: 'Windows/Doors' },
    { key: 'segment_wire_and_cable', label: 'Wire & Cable' },
    { key: 'segment_film_and_sheet', label: 'Film & Sheet' },
    { key: 'segment_fencing_and_decking', label: 'Fencing/Decking' },
    { key: 'segment_resellers_and_compounders', label: 'Resellers & Compounders' },
    { key: 'segment_all_other_uses', label: 'Other Uses' },
  ],
};

interface MarketsViewProps {
  polymer: PolymerKey;
  data: any[];
}

const MarketsView: React.FC<MarketsViewProps> = ({ polymer, data }) => {
  const rs = useRsColors();
  const { isDark } = useTheme();
  const [isCompactLegend, setIsCompactLegend] = useState(false);
  const [isCompactLegendPVC, setIsCompactLegendPVC] = useState(false);
  const [isCompactLegendPP, setIsCompactLegendPP] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    setIsCompactLegend(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsCompactLegend(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    if (polymer !== 'PVC') return;
    const mq = window.matchMedia('(max-width: 1140px)');
    setIsCompactLegendPVC(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsCompactLegendPVC(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [polymer]);

  useEffect(() => {
    if (polymer !== 'PP') return;
    const mq = window.matchMedia('(max-width: 750px)');
    setIsCompactLegendPP(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsCompactLegendPP(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [polymer]);

  const hoverLinePlugin = useMemo(() => createVerticalHoverLinePlugin(rs), [rs, isDark]);
  const segments = SEGMENT_CONFIGS[polymer] || [];
  const sorted = keepFirstEntryPerMonth(data);

  const labels = sorted.map((d) => {
    const dateString = String(d.date || d.record_date).slice(0, 10);
    try {
      return format(new Date(dateString + 'T00:00:00'), 'MMM yyyy');
    } catch {
      return dateString;
    }
  });

  const datasets: StackedDataset[] = segments.map((seg) => ({
    label: seg.label,
    data: sorted.map((d) => {
      const v = d[seg.key];
      return v !== null && v !== undefined ? Number(v) : 0;
    }),
    ...(seg.color ? { color: seg.color } : {}),
    ...(seg.dataLabelColor ? { dataLabelColor: seg.dataLabelColor } : {}),
  }));

  return (
    <div className={`flex flex-col gap-4 ${isCompactLegend ? 'p-0 -mx-6' : 'p-4'}`}>
      <div className={`bg-rs-off-black rounded-lg ${isCompactLegend ? 'p-0' : 'p-4'} ${isDark ? '' : ''}`}>
        <h3 className={`text-rs-off-white font-semibold mb-3 text-sm ${isCompactLegend || (polymer === 'PVC' && isCompactLegendPVC) || (polymer === 'PP' && isCompactLegendPP) ? 'px-4 pt-4 text-center' : ''}`}>
          Market Segments
        </h3>
        {((polymer === 'PVC' && isCompactLegendPVC) || (polymer === 'PP' && isCompactLegendPP)) && (
          <div className="flex justify-center mb-3">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 w-fit">
              {datasets.map((ds, i) => (
                <div key={ds.label} className="flex items-center gap-2">
                  <span
                    className="inline-block w-3 h-3 rounded-sm flex-shrink-0"
                    style={{ backgroundColor: ds.color || chartColors.getColorByIndex(i) }}
                  />
                  <span className="text-rs-off-white text-xs whitespace-nowrap">{ds.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {data.length === 0 ? (
          <div className="h-[700px] flex items-center justify-center text-rs-off-white/40 text-sm">No markets data</div>
        ) : (
          <ChartDateLimiter labels={labels} datasets={datasets} >
            {(limitedLabels, limitedDatasets) => (
              <div className={`h-[700px] ${isCompactLegend || isCompactLegendPVC || isCompactLegendPP ? 'px-4 pb-4' : ''}`}>
                <StackedBarChart
                  datasets={limitedDatasets as StackedDataset[]}
                  labels={limitedLabels}
                showRangeSlider={false}
                showDataLabels={false}
                plugins={[hoverLinePlugin]}
                className="w-full h-full"
                hideLegend={(polymer === 'PVC' && isCompactLegendPVC) || (polymer === 'PP' && isCompactLegendPP)}
              />
            </div>
          )}
        </ChartDateLimiter>
        )}
      </div>

      <p className={`text-rs-highlight-gray text-xs ${isCompactLegend ? 'px-4' : ''}`}>
        Source: American Chemistry Council® (ACC)
      </p>
    </div>
  );
};

export default MarketsView;
